// tokenManager.js

export const tokenManager = {
    
    setTokens: (accessToken) => {
        // Only store access token in localStorage (15 min validity)
        localStorage.setItem('access_token', accessToken);
        // Don't store refresh token anywhere - generate on demand
        
        // Store timestamp when token was set
        localStorage.setItem('token_timestamp', Date.now().toString());
    },

    getAccessToken: () => localStorage.getItem('access_token'),
    
    clearTokens: () => {
        // Clear all authentication-related data from localStorage
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        localStorage.removeItem('token_timestamp');
        
        // Clear any other potential auth-related items
        localStorage.removeItem('refresh_token'); // In case any old data exists
        localStorage.removeItem('admin');         // In case any old admin data exists
        
        // Optional: Clear all localStorage if you want complete cleanup
        // localStorage.clear(); // Uncomment if you want to clear everything
    },

    setUser: (user) => {
        localStorage.setItem('user', JSON.stringify(user));
    },

    getUser: () => {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    },

    // Check if user is authenticated AND token is valid
    isAuthenticated: () => {
        const token = localStorage.getItem('access_token');
        const user = tokenManager.getUser();

        if (!token) {
            return false;
        }

        // If token exists and is not expired, user is authenticated
        if (!tokenManager.isTokenExpired()) {
            return true;
        }

        // If token is expired but we have user data, we can attempt refresh
        if (user && user.id && user.role) {
            console.log('🔴 Token expired but user data exists - refresh possible');
            return true; // Let the axios interceptor handle the refresh
        }

        // No valid token and no user data to refresh with
        console.log('🔴 No valid token or user data - not authenticated');
        return false;
    },


    isAdmin: () => {
        const user = tokenManager.getUser();
        return user && user.role === 'admin';
    },

    // Decode JWT token
    decodeToken: (token) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    },

    // Get token expiration from JWT
    getTokenExpiration: () => {
        const token = tokenManager.getAccessToken();
        if (!token) return null;
        
        const decoded = tokenManager.decodeToken(token);
        return decoded ? decoded.exp * 1000 : null; // Convert to milliseconds
    },

    // Get time until token expires (in milliseconds) - for debugging only
    getTimeUntilExpiry: () => {
        const expiration = tokenManager.getTokenExpiration();
        if (!expiration) {
            // Fallback to timestamp-based calculation
            const timestamp = localStorage.getItem('token_timestamp');
            if (!timestamp) return 0;
            
            const tokenAge = Date.now() - parseInt(timestamp);
            const fifteenMinutes = 15 * 60 * 1000;
            return Math.max(0, fifteenMinutes - tokenAge);
        }
        
        return Math.max(0, expiration - Date.now());
    },

    // Check if token is expired - for debugging only
    isTokenExpired: () => {
        return tokenManager.getTimeUntilExpiry() <= 0;
    },

    // Check if token needs refresh (13 minutes - refresh 2 min before expiry)
    shouldRefreshToken: () => {
        const timestamp = localStorage.getItem('token_timestamp');
        if (!timestamp) return false;
        
        const tokenAge = Date.now() - parseInt(timestamp);
        const thirteenMinutes = 13 * 60 * 1000; // 13 minutes in milliseconds
        
        return tokenAge > thirteenMinutes;
    },

    // Check if token expires within specified minutes
    expiresWithin: (minutes) => {
        const timeUntilExpiry = tokenManager.getTimeUntilExpiry();
        const targetTime = minutes * 60 * 1000; // Convert minutes to milliseconds
        return timeUntilExpiry <= targetTime;
    }
};
  